import { Box, Tooltip, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useMemo } from 'react';

/**
 * Check data structure for uptime calculation
 */
interface CheckData {
  status: 'UP' | 'DOWN';
  createdAt: Date | string;
}

/**
 * Day data for uptime visualization
 */
interface DayData {
  date: Date;
  totalChecks: number;
  successfulChecks: number;
  uptimePercentage: number;
}

/**
 * UptimeBar component props
 */
interface UptimeBarProps {
  checks: CheckData[];
  days?: number;
}

/**
 * Get color based on uptime percentage
 */
function getUptimeColor(uptimePercentage: number, hasData: boolean): string {
  if (!hasData) return '#E5E7EB'; // Gray for no data
  if (uptimePercentage === 100) return '#10B981'; // Green for 100%
  if (uptimePercentage >= 95) return '#34D399'; // Light green for 95-99%
  if (uptimePercentage >= 80) return '#FBBF24'; // Yellow for 80-94%
  if (uptimePercentage >= 50) return '#F59E0B'; // Amber for 50-79%
  return '#EF4444'; // Red for < 50%
}

/**
 * Format date for display
 */
function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Group checks by day
 */
function groupChecksByDay(checks: CheckData[], days: number): DayData[] {
  const now = new Date();
  const dayMap = new Map<string, { total: number; successful: number }>();

  // Initialize all days
  for (let i = 0; i < days; i++) {
    const date = new Date(now);
    date.setDate(date.getDate() - (days - 1 - i));
    date.setHours(0, 0, 0, 0);
    const isoString = date.toISOString();
    const key = isoString.split('T')[0] ?? isoString;
    dayMap.set(key, { total: 0, successful: 0 });
  }

  // Group checks
  checks.forEach((check) => {
    const checkDate = new Date(check.createdAt);
    checkDate.setHours(0, 0, 0, 0);
    const isoString = checkDate.toISOString();
    const key = isoString.split('T')[0] ?? isoString;

    if (dayMap.has(key)) {
      const dayData = dayMap.get(key)!;
      dayData.total += 1;
      if (check.status === 'UP') {
        dayData.successful += 1;
      }
    }
  });

  // Convert to array
  const result: DayData[] = [];
  for (let i = 0; i < days; i++) {
    const date = new Date(now);
    date.setDate(date.getDate() - (days - 1 - i));
    date.setHours(0, 0, 0, 0);
    const isoString = date.toISOString();
    const key = isoString.split('T')[0] ?? isoString;
    const data = dayMap.get(key) || { total: 0, successful: 0 };

    result.push({
      date,
      totalChecks: data.total,
      successfulChecks: data.successful,
      uptimePercentage: data.total > 0 ? Math.round((data.successful / data.total) * 100) : 0,
    });
  }

  return result;
}

/**
 * UptimeBar component
 * Displays a 90-day uptime visualization with bars for each day
 */
export function UptimeBar({ checks, days = 90 }: UptimeBarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Calculate display days based on screen size
  const displayDays = isMobile ? 30 : isTablet ? 60 : days;

  // Group checks by day
  const dayData = useMemo(() => groupChecksByDay(checks, displayDays), [checks, displayDays]);

  // Calculate overall uptime
  const overallUptime = useMemo(() => {
    const totalChecks = checks.length;
    if (totalChecks === 0) return null;
    const successfulChecks = checks.filter((c) => c.status === 'UP').length;
    return Math.round((successfulChecks / totalChecks) * 10000) / 100;
  }, [checks]);

  return (
    <Box>
      {/* Uptime summary */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {displayDays} day history
        </Typography>
        {overallUptime !== null && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: overallUptime >= 99 ? '#10B981' : overallUptime >= 95 ? '#F59E0B' : '#EF4444',
            }}
          >
            {overallUptime.toFixed(2)}% uptime
          </Typography>
        )}
      </Box>

      {/* Uptime bars */}
      <Box
        sx={{
          display: 'flex',
          gap: '2px',
          height: 32,
          alignItems: 'flex-end',
        }}
      >
        {dayData.map((day, index) => {
          const hasData = day.totalChecks > 0;
          const color = getUptimeColor(day.uptimePercentage, hasData);

          return (
            <Tooltip
              key={index}
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(day.date)}
                  </Typography>
                  {hasData ? (
                    <>
                      <Typography variant="caption" display="block">
                        Uptime: {day.uptimePercentage}%
                      </Typography>
                      <Typography variant="caption" display="block">
                        {day.successfulChecks}/{day.totalChecks} checks passed
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No data available
                    </Typography>
                  )}
                </Box>
              }
              arrow
              placement="top"
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 4,
                  maxWidth: 8,
                  height: hasData ? '100%' : '50%',
                  backgroundColor: color,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  transition: 'opacity 0.2s, transform 0.2s',
                  '&:hover': {
                    opacity: 0.8,
                    transform: 'scaleY(1.1)',
                  },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* Date labels */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 0.5,
        }}
      >
        <Typography variant="caption" color="text.disabled">
          {displayDays} days ago
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Today
        </Typography>
      </Box>
    </Box>
  );
}

export default UptimeBar;
